<template>
  <div class="data-validation-container">
    <ProgressTracker ref="progressTracker" />
    <div
      v-if="isCompleted"
      class="parsed-data">
      <PanelTemplate
        v-if="isValid"
        :icon="markRaw(IconSuccess)"
        :color="'success'">
        {{ i18n.t('messages.dataReadyToSync') }}
      </PanelTemplate>
      <template v-else-if="!isEmpty">
        <PanelTemplate
          :icon="markRaw(IconWarning)"
          :color="'warning'">
          <span class="error-header">
            <em>{{ i18n.t('messages.invalidItemsInSyncWithSpreadsheet') }}</em>
            <HSButton @click="excludeInvalidItems">{{ i18n.t('actions.excludeInvalidItems') }}</HSButton>
          </span>
        </PanelTemplate>
        <ValidationErrors :validationErrors="validationErrors" />
      </template>

      <HSTable
        :id="tableId"
        :enable-search-section="false"
        :enable-column-reorder="false"
        :is-editable="false"
        :allow-selection="false"
        :show-filters="false"
        :items="parsedEntities"
        :is-loading="false"
        category="bulk-additions"
        :columns="columns"
        :show-raw-values="true"
        :validation-errors="validationErrors"
        :show-error-summary="false" />
    </div>
  </div>

  <ConfirmModal ref="confirmModal" />
</template>

<script setup lang="ts" generic="T extends IBulkCreateEntity">
  import Excel from 'exceljs';
  import { computed, markRaw, onMounted, ref, useTemplateRef, watch } from 'vue';
  import { useI18n } from 'vue-i18n';

  import ConfirmModal from '@/common/components/controls/ConfirmModal.vue';
  import ValidationErrors from '@/common/components/controls/table/bulk-common/ValidationErrors.vue';
  import { IBulkCreateEntity } from '@/common/components/controls/table/bulk-create/BulkCreateEntity';
  import { SpreadSheetModelParser } from '@/common/components/controls/table/bulk-create/model-parser/ModelParser';
  import HSTable from '@/common/components/controls/table/Table.vue';
  import { IValidationError } from '@/common/components/controls/table/TableProps';
  import FormArray from '@/common/components/generic-form/form-controls/FormArray';
  import FormControl from '@/common/components/generic-form/form-controls/FormControl';
  import PanelTemplate from '@/common/components/templates/PanelTemplate.vue';
  import HSButton from '@/common/components/ui/Button/Button.vue';
  import ProgressTracker from '@/common/components/ui/ProgressTracker.vue';
  import { IconSuccess, IconWarning } from '@/common/icons/monochrome';

  const i18n = useI18n();

  interface IProps {
    modelParser: SpreadSheetModelParser<T>;
    worksheet: Excel.Worksheet;
  }

  const { modelParser, worksheet } = defineProps<IProps>();

  interface IEmits {
    (e: 'entities-parsed', value: T[]): void;
  }

  const emits = defineEmits<IEmits>();

  // Template Refs
  const confirmModal = useTemplateRef<InstanceType<typeof ConfirmModal>>('confirmModal');
  const progressTracker = useTemplateRef<InstanceType<typeof ProgressTracker>>('progressTracker');

  // Data Refs
  const validationErrors = ref<IValidationError<T>[]>();
  const formArray = ref<FormArray<T>>();
  const parsedEntities = ref<T[]>();
  const isCompleted = ref(false);

  const isValid = computed(() => {
    return (validationErrors.value == null || validationErrors.value.length === 0) && !isEmpty.value;
  });
  const isEmpty = computed(() => {
    return parsedEntities.value?.length === 0;
  });

  const tableId = computed(() => `sync-with-spreadsheet-table-${modelParser.modelName}`);
  const columns = computed(() => modelParser.tableColumns);

  async function parseWorksheet() {
    if (worksheet == null) {
      return;
    }
    const progressHandler = progressTracker.value.init();
    validationErrors.value = [];
    try {
      const controls = new Array<FormControl<T>>();
      const dataRows = modelParser.getDataRows(worksheet);
      progressHandler.start(dataRows.length);
      for (let i = 0; i < dataRows.length; i++) {
        const row = dataRows[i];
        progressHandler.next(
          `${i18n.t('messages.processingRow')} ${progressHandler.currentStepID + 1}/${progressHandler.totalSteps}`,
        );
        const control = await modelParser.parseRow(row);
        await control.runValidators();
        controls.push(control);
        if (control.invalid) {
          validationErrors.value.push({
            rowId: i + 1,
            entity: control.value,
            errors: control.errors,
            errorMessages: control.errorMessages,
          });
        }
      }
      const parsedFormArray = new FormArray(controls);
      parsedEntities.value = parsedFormArray.value;
      formArray.value = parsedFormArray;
    } finally {
      progressHandler.finish();
      isCompleted.value = true;
    }
  }

  async function excludeInvalidItems() {
    const nbOfInvalidItems = validationErrors.value.length;
    const confirmation = await confirmModal.value.confirm({
      title: i18n.t('messages.excludeInvalidItems'),
      message: i18n.t('messages.excludeInvalidItemsConfirmation', [nbOfInvalidItems]),
    });
    if (!confirmation) {
      return;
    }
    parsedEntities.value = formArray.value.controls
      .filter((control) => !control.invalid)
      .map((control) => control.value);
    validationErrors.value = [];
  }

  watch(parsedEntities, () => {
    if (isValid.value) {
      emits('entities-parsed', parsedEntities.value);
    }
  });

  onMounted(() => {
    parsedEntities.value = null;
    validationErrors.value = [];
    parseWorksheet();
  });
</script>

<style scoped lang="scss">
  .data-validation-container {
    width: 100%;
    min-height: 20rem;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .parsed-data {
    display: block;
    width: 100%;
  }

  .error-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: 100%;
  }
</style>
